import { Box, Grid, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import * as React from "react";
import { useEffect, useState } from "react";
import { initialAppState } from "../../common/reducers/appState";
import { fetchData } from "../../common/services/ApiClient";
import { fileNameFormat, toTitleCase } from "../../common/services/Utilities";
import { ApiBaseUrl } from "../../config";
import ReportingPeriod from "../../containers/ReportingPeriod";
import SearchPills from "../../containers/SearchPills";
import useGetTop100Certs from "../../hooks/useGetTop100Certs";
import usePagination from "../../hooks/usePagination";
import {
  KeyStringValue,
  KeyValue,
  Metadata,
  Query,
  SearchPillData,
  SimpleTableRow,
  Sort,
  Styles,
  TableHeader,
} from "../../interfaces";
import ApiDataDownload, { apiDownloadProps } from "../ApiDataDownload";
import { financialReportingSearchPills } from "../FinancialReporting/FinancialReportingLevelOne";
import {
  generateFinancialsApiUrl,
  getReportPeriod,
  reportPeriodDateFormat,
} from "../FinancialReporting/FinancialReportingUtilities";
import LoadingIndicator from "../LoadingIndicator";
import MultiHeaderTable from "../MultiHeaderTable/MultiHeaderTable";
import SimpleTableBody from "../MultiHeaderTable/SimpleTableBody";
import ResultPagination from "../ResultPagination";
import ShowHideButton from "../ShowHideButton";

const PREFIX = "PeerGroupCriteriaTable";

const classes = {
  peerGroupPills: `${PREFIX}-peerGroupPills`,
  h4: `${PREFIX}-h4`,
  reportPeriodBox: `${PREFIX}-reportPeriodBox`,
  header: `${PREFIX}-header`,
  modalContent: `${PREFIX}-modalContent`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.peerGroupPills}`]: {
    "& .MuiCollapse-root": {
      marginTop: -25,
      marginBottom: 10,
    },
    "& #topLevelPagination": {
      width: "95%",
    },
  },

  [`& .${classes.h4}`]: {
    color: "#1A4480",
    padding: "0",
    fontSize: "1rem",
    fontWeight: "bold",
    lineHeight: "1.5rem",
    paddingBottom: "1rem",
  },

  [`& .${classes.reportPeriodBox}`]: {
    "backgroundColor": theme.palette.secondary.main,
    "maxWidth": 400,
    "float": "right",
    "padding": ".25rem .5rem",
    "& p": {
      color: theme.palette.primary.main,
      fontWeight: "bold",
      textAlign: "right",
      lineHeight: "1.25rem",
      padding: "4px 0",
    },
    "& .MuiFormControl-marginDense": {
      marginTop: 4,
    },
  },

  [`& .${classes.header}`]: {
    paddingBottom: 0,
  },

  [`& .${classes.modalContent}`]: {},
}));

interface Headers {
  title: string;
  apiField: string;
  subTitle?: string;
}

interface Props {
  currentGroup: KeyValue;
  appState: Query;
  reportDate?: string;
  includeReportPeriod?: boolean;
}

const HeadersMetaData: Headers[] = [
  { title: "Cert/ID", apiField: "CERT" },
  { title: "Institution Name", apiField: "NAME" },
  { title: "City", apiField: "CITY" },
  { title: "State", apiField: "STALP" },
  { title: "Class", apiField: "BKCLASS" },
  {
    title: "Total Assets",
    subTitle: "(Thousands USD)",
    apiField: "ASSET",
  },
  {
    title: "Total Deposits",
    subTitle: "(Thousands USD)",
    apiField: "DEP",
  },
];

const PeerGroupHeaders: TableHeader[][] = [
  HeadersMetaData.map((header: Headers) => {
    const headerStyles: { [key: string]: Styles } = {
      NAME: { textAlign: "left" },
      CITY: { textAlign: "left" },
    };
    return {
      title: header.title,
      subTitle: header.subTitle,
      styles: { ...headerStyles[header.apiField], width: "full-width" },
      colSpan: 1,
      apiField: header.apiField,
    };
  }),
];

export function formattedPeerGroupData(apiField: string, data: KeyValue) {
  switch (apiField) {
    case "CITY":
      return toTitleCase(data.CITY);
    case "DEP":
    case "ASSET":
      return data[apiField] ? data[apiField].toLocaleString() : "N/A";
    default:
      return data[apiField];
  }
}

export const transformDataToTableBodyData = (peerGroups: KeyValue[]) => {
  return peerGroups.map((data: KeyValue) => {
    const tableCellData: SimpleTableRow = {};
    const tableStylesLookup: { [key: string]: Styles } = {
      NAME: {
        textAlign: "left",
        fontWeight: "bold",
        color: "blue",
        textDecoration: "underline",
        cursor: "pointer",
      },
      CITY: { textAlign: "left" },
    };
    HeadersMetaData.map((value: Headers) => value.apiField).forEach(
      (apiField: string) => {
        tableCellData[apiField] = {
          value: formattedPeerGroupData(apiField, data),
          styles: tableStylesLookup[apiField]
            ? tableStylesLookup[apiField]
            : {},
        };
      },
    );

    return tableCellData;
  });
};

export default function PeerGroupCriteriaTable({
  currentGroup,
  appState,
  reportDate,
  includeReportPeriod,
}: Props) {
  const [peerGroupData, setPeerGroupData] = useState<SimpleTableRow[]>([]);
  const [latestSODYear, setLatestSODYear] = React.useState<number>();

  const [resultCount, setResultCount] = useState<Metadata>({
    count: 0,
  });
  const [sort, setSort] = useState<Sort>({
    sortField: "CERT",
    sortOrder: "ASC",
  });

  const [report, setReport] = React.useState({
    reportPeriod: [
      (currentGroup.appState?.reportPeriod ??
      currentGroup.appState?.filters?.reportPeriod?.[0])
        ? currentGroup.appState.filters.reportPeriod[0]
        : reportDate || "",
    ],
  });
  const [urlDownload, setUrlDownload] = React.useState("");

  const {
    page,
    rowsPerPage,
    handlePageJump,
    handleChangePage,
    handleChangeRowsPerPage,
  } = usePagination(1, 25);

  const noLabelValues: any = (value: string) => {
    const assetConcentrationLookup: KeyStringValue = {
      "1": "International Banks",
      "2": "Agriculture Banks",
      "3": "Credit Card Banks",
      "4": "Commercial Lenders",
      "5": "Mortgage Lenders",
      "6": "Consumer Lenders",
      "7": "All Other Specialized Maximum: $1,000,000,000",
      "8": "All Other Maximum: $1,000,000,000",
      "9": "All Other Greater Minimum: $1,000,000,000",
    };
    const geographicRegionLookup: KeyStringValue = {
      "02": "Geographic Region: New York",
      "05": "Geographic Region: Atlanta",
      "09": "Geographic Region: Chicago",
      "11": "Geographic Region: Kansas City",
      "13": "Geographic Region: Dallas",
      "14": "Geographic Region: San Francisco",
    };

    return {
      ACTIVE: "Active Institutions",
      INSCOML: "Commercial Banks",
      INSSAVE: "Savings Institutions",
      SPECGRP: assetConcentrationLookup[value],
      FDICDBS: geographicRegionLookup[value],
      FEDCHRTR: "Federal Charters",
      STCHRTR: "State Charters",
      SZ100: "Assets: Less than $100M",
      SZ100T1B: "Assets: $100,000,000 — $1,000,000,000",
      SZ1BT10B: "Assets: $1,000,000,000 — $10,000,000,000",
      S10T250B: "Assets: $10,000,000,000 — $250,000,000,000",
      SZ250BP: "Assets: Greater than $250,000,000,000",
    };
  };
  React.useEffect(() => {
    let isMounted = true;

    if (!latestSODYear) {
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/sod?sort_by=YEAR&sort_order=DESC&limit=1&fields=YEAR`,
        )
          .then((response) => {
            if (isMounted) {
              setLatestSODYear(response.data?.[0]?.YEAR);
            }
          })
          .catch((error) => {
            console.error(`${error.name} : ${error.message}`);
          })
          .finally(() => {
            isMounted = false;
          });
      })();
    }
  }, [latestSODYear]);

  React.useEffect(() => {
    if (
      report.reportPeriod[0] === "" &&
      (!currentGroup.appState ||
        currentGroup.appState.filters?.reportPeriod?.[0] === "")
    ) {
      let isMounted = true;
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/financials?limit=1&offset=0&sort_by=RISDATE&sort_order=DESC&fields=REPDTE`,
        )
          .then((response) => {
            if (isMounted) {
              setReport({
                reportPeriod: [
                  response.data[0].REPDTE
                    ? response.data[0].REPDTE
                    : response.data[0].RISDATE,
                ],
              });
            }
          })
          .catch(() => {
            // if error set to current quarter
            const currentDate = getReportPeriod(new Date());
            setReport({
              reportPeriod: [currentDate[2] + currentDate[0] + currentDate[1]],
            });
          });
      })();
      return () => {
        isMounted = false;
      };
    }
  }, [currentGroup.appState, report]);

  const peerGroupSearchPills: SearchPillData[] =
    currentGroup.appState?.filters &&
    typeof currentGroup.appState.filters === "object" &&
    !Array.isArray(currentGroup.appState.filters)
      ? financialReportingSearchPills({
          ...currentGroup.appState,
          filters: {
            ...currentGroup.appState.filters,
            reportPeriod: report.reportPeriod,
          },
        }).map((searchPill: SearchPillData) => {
          return { ...searchPill, static: true };
        })
      : [
          {
            value: reportPeriodDateFormat(report.reportPeriod[0]),
            search: false,
            formKey: "",
            static: true,
            label: "Reporting Period",
          },
          ...currentGroup.filters.map((filter: KeyValue) => {
            return {
              value: Object.keys(noLabelValues(filter.value)).includes(
                filter.variable,
              )
                ? noLabelValues(filter.value)[filter.variable]
                : isNaN(filter.value)
                  ? filter.value
                  : `$${(filter.value * 100000).toLocaleString()}`,

              static: true,
              search: false,
              formKey: "",
              reportPeriod: report.reportPeriod,
            };
          }),
        ];

  const [apiCallFinished, setApiCallFinished] = React.useState(false);

  const top100Certs = useGetTop100Certs(currentGroup.appState);

  const [previousCommonSearch, setPreviousCommonSearch] = React.useState("");
  const [certsNotInTable, setCertsNotInTable] = React.useState<string[]>([]);

  const unavailableCertPills = certsNotInTable.map((cert: string) => {
    return {
      value: cert,
      search: false,
      formKey: "cert",
      static: true,
      label: "Cert",
    };
  });

  useEffect(() => {
    if (
      top100Certs.length > 0 &&
      currentGroup.appState &&
      currentGroup.appState.filters.commonSearch !== previousCommonSearch
    ) {
      setPreviousCommonSearch(currentGroup.appState.filters.commonSearch);
      setSort({ sortField: "ASSET", sortOrder: "DESC" });
    }
  }, [top100Certs.length, currentGroup.appState, previousCommonSearch]);

  useEffect(() => {
    let isMounted = true;
    const institutionsFilters = [
      `RISDATE:${report.reportPeriod[0]}`,
      ...currentGroup.filters.map(
        (filter: KeyValue) =>
          `${filter.variable}:${
            filter.operator !== "=" ? filter.operator : ""
          }${filter.value}`,
      ),
    ].join(" AND ");

    const groupAppState = currentGroup.appState && {
      ...currentGroup.appState,
      filters: {
        ...currentGroup.appState.filters,
        reportPeriod: report.reportPeriod,
        multiCert:
          top100Certs.length > 0
            ? top100Certs
            : currentGroup.appState.filters.multiCert,
      },
    };

    const headerWithReportPeriod = [
      {
        title: "Report Period",
        apiField: "RISDATE",
      },
      ...HeadersMetaData,
    ];

    const fetchUrl =
      report && !currentGroup.appState && report.reportPeriod[0]
        ? `${ApiBaseUrl()}/financials?filters=${institutionsFilters}&fields=${headerWithReportPeriod
            .map((header) => header.apiField)
            .join()}`
        : currentGroup.appState
          ? generateFinancialsApiUrl(
              groupAppState,
              headerWithReportPeriod.map((header) => header.apiField),
            )
          : undefined;
    if (currentGroup.title && fetchUrl) {
      setUrlDownload(fetchUrl as string);
      (async () => {
        await fetchData(
          `${fetchUrl}&sort_by=${
            sort.sortField
          }&sort_order=${sort.sortOrder.toLowerCase()}&limit=${rowsPerPage}&offset=${
            (page - 1) * rowsPerPage
          }`,
        )
          .then((response) => {
            if (isMounted) {
              setPeerGroupData(transformDataToTableBodyData(response.data));
              setResultCount(response.totals);
            }
          })
          .catch((error) => {
            console.error(`${error.name} : ${error.message}`);
          })
          .finally(() => {
            setApiCallFinished(true);
          });
      })();
    }
    return () => {
      isMounted = false;
    };
  }, [
    currentGroup.appState,
    currentGroup.filters,
    currentGroup.title,
    page,
    report,
    rowsPerPage,
    sort.sortField,
    sort.sortOrder,
    top100Certs,
  ]);
  useEffect(() => {
    let isMounted = true;

    if (
      currentGroup.appState &&
      currentGroup.appState.filters.multiCert &&
      (report.reportPeriod[0] !== "" ||
        currentGroup.appState.filters.reportPeriod[0] !== "")
    ) {
      const groupAppState = currentGroup.appState && {
        ...currentGroup.appState,
        filters: {
          ...currentGroup.appState.filters,
          reportPeriod: report.reportPeriod,
          multiCert: currentGroup.appState.filters.multiCert,
        },
      };
      const fetchUrl = generateFinancialsApiUrl(groupAppState);
      (async () => {
        await fetchData(`${fetchUrl}&limit=10000&offset=0`)
          .then((response) => {
            if (isMounted) {
              const currentCerts = response.data
                .map((data: KeyValue) =>
                  data.CERT ? data.CERT.toString() : "",
                )
                .filter((cert: string) => cert !== "");
              setCertsNotInTable(
                currentGroup.appState.filters.multiCert.filter(
                  (cert: string) => currentCerts.indexOf(cert) === -1,
                ),
              );
            }
          })
          .catch((error) => {
            console.error(`${error.name} : ${error.message}`);
          });
      })();
    }
    return () => {
      isMounted = false;
    };
  }, [currentGroup.appState, report.reportPeriod]);

  function sortHandler(tableHeader: TableHeader) {
    if (tableHeader.apiField) {
      const sortDirection =
        sort.sortField === tableHeader.apiField && sort.sortOrder === "ASC"
          ? "DESC"
          : "ASC";
      setSort({ sortField: tableHeader.apiField, sortOrder: sortDirection });
    }
  }

  const reportPeriodDisplay = (
    <Grid container spacing={2}>
      <Grid size={7}>
        <Typography
          color="primary.main"
          variant="h2"
          style={{ paddingBottom: 8 }}
        >
          Peer Group Population Preview
        </Typography>
        <Typography color="primary.main" variant="h3">
          {resultCount.count.toLocaleString()}{" "}
          {resultCount.count === 1 && includeReportPeriod
            ? "Active Institution"
            : resultCount.count > 1 && includeReportPeriod
              ? "Active Institutions"
              : ""}
          {" for Reporting Period"}
        </Typography>
      </Grid>
      <Grid size={5}>
        <Box className={classes.reportPeriodBox}>
          <Grid container spacing={2}>
            <Grid size={4}>
              <Typography variant="body1">
                Adjust Preview Reporting Period
              </Typography>
            </Grid>
            <Grid size={8}>
              <ReportingPeriod
                managepgc
                currentQuarterValue={report.reportPeriod[0]?.substring(4, 8)}
                currentYearValue={report.reportPeriod[0]?.substring(0, 4)}
                setForm={setReport}
                form={report}
              />
            </Grid>
          </Grid>
        </Box>
      </Grid>
    </Grid>
  );

  const title = (
    <Grid container spacing={2}>
      <Grid size={12}>
        <Typography
          className={classes.h4}
          component="div"
          id="peer-group-count"
        >
          {includeReportPeriod ? (
            <Root style={{ marginTop: -8 }}>
              Definition Method:
              {currentGroup.isMulti
                ? " Explicit Search List"
                : " Dynamic Search Criteria"}
            </Root>
          ) : (
            `${resultCount.count.toLocaleString()} ${
              resultCount.count === 1 ? "Matching Result" : "Matching Results"
            }`
          )}
        </Typography>
      </Grid>
    </Grid>
  );

  const searchPillsDisplay = (
    <div id="bankfind-search-pills-container">
      <ShowHideButton
        title={title}
        body={<SearchPills dataArray={peerGroupSearchPills} />}
      />
    </div>
  );

  const peerGroupDownloadHeaders = PeerGroupHeaders[0].map(
    (header: KeyValue) => {
      return {
        label: header.title,
        key: header.apiField,
      };
    },
  );
  const downloadHeaders = [
    { label: "Report Period", key: "RISDATE" },
    ...peerGroupDownloadHeaders,
  ];

  const year = report?.reportPeriod[0]?.slice(0, 4);
  const initAppState = {
    ...initialAppState.SOD,
    filters: {
      ...initialAppState.SOD.filters,
      lastYear: latestSODYear && year > latestSODYear ? latestSODYear : year,
      stalpbr: appState.filters?.stalp,
      zipbr: appState.filters?.zip,
    },
  };
  const apiData: apiDownloadProps[] = [
    // {
    //   appState,
    //   fileName: `${fileNameFormat("PeerGroups_L1_raw")}.csv`,
    //   apiPath: "financials",
    //   text: "Download Results Data - raw",
    //   data: appState.ui.reportDownload,
    // },
    {
      appState,
      fileName: `${fileNameFormat("PeerGroups_L1_formatted")}.csv`,
      apiPath: "financials",
      text: "Download Results Data - formatted",
      headers: downloadHeaders,
      downloadUrl: `${urlDownload}&sort_by=${
        sort.sortField
      }&sort_order=${sort.sortOrder.toLowerCase()}`,
    },
  ];

  return (
    <div id="peerGroupCriteria" className={classes.peerGroupPills}>
      {includeReportPeriod && reportPeriodDisplay}
      {searchPillsDisplay}
      {certsNotInTable.length > 0 && (
        <Box
          sx={{
            marginBottom: 1,
          }}
        >
          <Typography
            variant="h4"
            color="error"
            style={{ paddingBottom: ".5rem" }}
          >
            The following certs do not exist for this report period:
          </Typography>
          <SearchPills dataArray={unavailableCertPills} errorColor />
        </Box>
      )}
      {peerGroupData.length > 0 ? (
        <ResultPagination
          count={resultCount.count}
          handleChangePage={handleChangePage as any}
          handlePickPage={handlePageJump}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          limit={rowsPerPage}
          pageNumber={page}
          hideBottomPagination
        >
          <div style={{ position: "relative", right: 0, top: -50 }}>
            <ApiDataDownload app="peerGroup" apiData={apiData} />
          </div>
          <MultiHeaderTable
            tableBody={
              <SimpleTableBody
                tableData={peerGroupData}
                appState={initAppState}
              />
            }
            tableHeaders={PeerGroupHeaders}
            sortProperties={{
              sortDirection: sort.sortOrder,
              sortField: sort.sortField,
              sortHandler,
            }}
          />
        </ResultPagination>
      ) : (
        <LoadingIndicator
          spinnerSx={{ textAlign: "center" }}
          textSx={{ textAlign: "center" }}
          spinnerSize={50}
          text="Loading"
          noResults={apiCallFinished}
        />
      )}
    </div>
  );
}
